import { useMemo, useRef } from 'react'
import { HOUR, isEmpty, isDefined, isNotDefined, messageFromError, type APIError } from '@terros/common'
import { type QueryKey, useInfiniteQuery, type InfiniteData } from '@tanstack/react-query'
import { useServerRequest } from '../api'
import { type GetInfiniteHookResponse, type InfinitePageResponse, type InfiniteQueryInputs } from './types'

export function useTantalimInfiniteQuery<TInput, TSuccess extends InfinitePageResponse, TRecord>({
  url,
  queryKey,
  input,
  selectRecords,
  buildNextInput,
  continueAfterEmptyPage,
  onPageFetched,
  placeholderData,
  options,
}: InfiniteQueryInputs<TInput, TSuccess, TRecord>): GetInfiniteHookResponse<TRecord> {
  const serverRequest = useServerRequest<TInput, TSuccess>(url)

  const query = useInfiniteQuery<TSuccess, APIError, InfiniteData<TSuccess>, QueryKey, TInput>({
    queryKey,
    // TanStack Query's QueryFunctionContext resolves pageParam as `unknown` when
    // TPageParam is an open generic — a known TypeScript limitation with conditional
    // types on unresolved type parameters. pageParam is always TInput at runtime
    // because we own both initialPageParam and getNextPageParam.
    queryFn: async (context) => {
      const pageParam = context.pageParam as TInput
      const result = await serverRequest(pageParam)
      onPageFetched?.(result)
      return result
    },
    initialPageParam: input,
    getNextPageParam: (lastPage, pages) =>
      getNextInfinitePageInput({ input, lastPage, pages, selectRecords, buildNextInput, continueAfterEmptyPage }),
    placeholderData,
    staleTime: HOUR,
    gcTime: 6 * HOUR,
    ...options,
  })

  const error = messageFromError(query.error)
  const selectRecordsRef = useRef(selectRecords)
  selectRecordsRef.current = selectRecords
  const records = useMemo(() => {
    return query.data?.pages.flatMap(selectRecordsRef.current) ?? []
  }, [query.data])
  const total = useMemo(() => {
    if (isEmpty(query.data)) return
    if (isEmpty(records)) return
    const max = Math.max(...query.data.pages.map((page) => page.total ?? 0))
    return Math.max(max, records.length)
  }, [query.data, records])

  return {
    ...query,
    error,
    data: records,
    total,
  }
}

type NextInfinitePageInputParams<TInput, TSuccess extends InfinitePageResponse, TRecord> = Pick<
  InfiniteQueryInputs<TInput, TSuccess, TRecord>,
  'input' | 'selectRecords' | 'buildNextInput' | 'continueAfterEmptyPage'
> & {
  lastPage: TSuccess
  pages: TSuccess[]
}

/** Input for the next page, or undefined once the cursor runs out or every record in `total` is loaded. */
export function getNextInfinitePageInput<TInput, TSuccess extends InfinitePageResponse, TRecord>({
  input,
  lastPage,
  pages,
  selectRecords,
  buildNextInput,
  continueAfterEmptyPage,
}: NextInfinitePageInputParams<TInput, TSuccess, TRecord>): TInput | undefined {
  const pageRecords = selectRecords(lastPage)
  if (isEmpty(pageRecords) && !continueAfterEmptyPage) return
  if (isNotDefined(lastPage.sortTimestamp)) return
  const pageRecordsCount = pages.reduce((acc, page) => acc + selectRecords(page).length, 0)
  if (isDefined(lastPage.total) && pageRecordsCount >= lastPage.total) return
  if (buildNextInput) {
    return buildNextInput(input, lastPage.sortTimestamp)
  }
  return { ...input, sortTimestamp: lastPage.sortTimestamp }
}
